"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, TrendingUp } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const metrics = [{
  label: "REVENUE UPLIFT",
  value: 32.5,
  suffix: "%",
  delta: "+ 4.2% MoM",
  positive: true,
  accent: false,
  offset: 0,
  sparkline: [12, 18, 14, 22, 19, 28, 24, 32, 29, 38, 35, 44]
}, {
  label: "OTA CONVERSION",
  value: 14.8,
  suffix: "%",
  delta: "+ 2.7% MoM",
  positive: true,
  accent: true,
  offset: 24,
  sparkline: [8, 12, 10, 16, 14, 20, 18, 24, 22, 28, 32, 36]
}, {
  label: "DIRECT BOOKINGS",
  value: 22.1,
  suffix: "%",
  delta: "+ 5.1% MoM",
  positive: true,
  accent: false,
  offset: 0,
  sparkline: [22, 26, 24, 28, 30, 28, 32, 30, 34, 36, 38, 40]
}, {
  label: "REVPAR GROWTH",
  value: 18.4,
  suffix: "%",
  delta: "+ 3.6% MoM",
  positive: true,
  accent: false,
  offset: 24,
  sparkline: [10, 14, 12, 18, 16, 22, 20, 26, 30, 28, 34, 38]
}];
function useCountUp(target: number, isVisible: boolean, duration = 1800) {
  const [value, setValue] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isVisible) return;
    startTimeRef.current = null;
    const tick = (ts: number) => {
      if (startTimeRef.current === null) startTimeRef.current = ts;
      const elapsed = ts - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, isVisible, duration]);
  return value;
}
function formatNumber(value: number, target: number, suffix: string) {
  if (suffix === "%" || suffix === "x") {
    return value.toFixed(1) + suffix;
  }
  if (target >= 1000) {
    return Math.floor(value).toLocaleString("en-US");
  }
  return Math.floor(value).toString();
}
function Sparkline({
  points,
  accent
}: {
  points: number[];
  accent: boolean;
}) {
  const width = 200;
  const height = 40;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const step = width / (points.length - 1);
  const path = points.map((p, i) => {
    const x = i * step;
    const y = height - (p - min) / range * height;
    return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(" ");
  return <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="w-full h-10" aria-hidden="true">
      <path d={path} fill="none" stroke={accent ? "var(--secondary)" : "var(--primary)"} strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="miter" />
    </svg>;
}
function StatCard({
  metric,
  index,
  isVisible
}: {
  metric: (typeof metrics)[number];
  index: number;
  isVisible: boolean;
}) {
  const animated = useCountUp(metric.value, isVisible);
  const display = formatNumber(animated, metric.value, metric.suffix);
  return <div data-index={index} className="group relative" style={{
    transform: `translateY(${metric.offset}px)`,
    transitionDelay: `${index * 90}ms`
  }}>
      <div className="relative bg-card border border-border shadow-md transition-all duration-300 hover:shadow-lg h-[320px] flex flex-col justify-between p-6 overflow-hidden rounded-lg">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-secondary transition-all duration-300 group-hover:shadow-md" aria-hidden="true" />

        <div className="flex items-start justify-between">
          <span className="font-default text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
            {metric.label}
          </span>
          <span className="font-default text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
            0{index + 1}
          </span>
        </div>

        <div className="flex-1 flex items-center">
          <span className={`font-default font-bold tabular-nums leading-none tracking-[-0.04em] text-[3.5rem] sm:text-[4rem] xl:text-[4.5rem] ${metric.accent ? "text-secondary" : "text-foreground"}`}>
            {display}
          </span>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-1.5">
            <ArrowUpRight className={`w-3.5 h-3.5 ${metric.positive ? "text-secondary" : "text-muted-foreground"}`} strokeWidth={2.5} />
            <span className={`font-default text-[11px] tracking-[0.12em] uppercase ${metric.positive ? "text-secondary" : "text-muted-foreground"}`}>
              {metric.delta}
            </span>
          </div>
          <Sparkline points={metric.sparkline} accent={metric.accent} />
        </div>
      </div>
    </div>;
}
export default function Metrics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="4037" id="propertysuccessmetrics" className="bg-background text-foreground relative py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "radial-gradient(var(--foreground) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} aria-hidden="true" />

      <div ref={ref} className="relative max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-12 md:mb-16">
          <span className="font-default text-xs tracking-[0.2em] uppercase text-secondary">
            02 / PERFORMANCE METRICS
          </span>
          <span className="h-px w-8 bg-secondary" aria-hidden="true" />
          <span className="font-default text-[10px] tracking-[0.2em] uppercase text-muted-foreground hidden sm:inline-flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" strokeWidth={2.5} />
            REVENUE IMPACT
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-24">
          <div className="lg:col-span-7">
            <h2 className="font-default font-bold text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-[-0.03em]">
              Revenue growth,{" "}
              <span className="text-secondary italic">quantified.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:flex lg:items-end">
            <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground max-w-[380px]">
              We transform hotel data into actionable revenue strategies. Our 
              proven methodology delivers measurable results for property owners 
              and investors seeking to maximize their bottom line.
            </p>
          </div>
        </div>

        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-5 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {metrics.map((metric, i) => <StatCard key={metric.label} metric={metric} index={i} isVisible={isVisible} />)}
        </div>

        <div className="mt-16 md:mt-24 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-8 border-t border-border">
          <span className="font-default text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
            METHODOLOGY / HOTELREVBABA REVENUE AUDIT FRAMEWORK
          </span>
          <span className="font-default text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-lg bg-secondary animate-ping opacity-75" />
              <span className="relative inline-flex rounded-lg h-1.5 w-1.5 bg-secondary" />
            </span>
            OPTIMIZING NOW
          </span>
        </div>
      </div>
    </section>;
}